import { defineStore } from 'pinia';
import {
  computed,
  reactive,
  ref,
} from 'vue';

import { DATE_FORMAT } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { TDomainError, TPagination } from '@/types';
import { TReachedResponse } from '@/api/types';
import {
  getNotifications,
  getUnreadNotificationsCount,
  readNotification,
  unreadNotification,
} from '@/contexts/notificationsContext/services';
import {
  TNotification,
  TNotificationsResponse,
  TUnreadNotificationsCountResponse,
} from '@/contexts/notificationsContext/domain/types';
import { normalizePayloadToDataIds } from '@/utils';

import {
  TNotifications,
  TNotificationsStore,
} from './types';

export const useNotificationStore = defineStore(
  'TMS_NOTIFICATIONS',
  (): TNotificationsStore => {
    const unreadCount = ref<number>(0);
    const isNotificationsListVisible = ref<boolean>(false);
    const isBrowserTabWithUnreadNotifications = ref<boolean>(false);
    const isPersonalizedNotificationsInfoVisible = ref<boolean>(false);
    const notifications = reactive<TNotifications>({
      ids: null,
      data: null,
    });

    const getGroupedNotificationsByDate = computed(() => {
      if (!notifications.ids) return [];
      const notificationsByDates = notifications.ids.reduce((dates, notificationId) => {
        const notification = notifications.data?.[notificationId];
        if (!notification) return dates;

        const notificationDate = formatDate(
          notification.createdAt, { outputFormat: DATE_FORMAT.fullDateWithShortYears },
        );

        if (!Array.isArray(dates[notificationDate])) {
          dates[notificationDate] = [notification];
        } else {
          dates[notificationDate].push(notification);
        }
        return dates;
      }, {} as Record<string, TNotification[]>);

      return Object.entries(notificationsByDates).map((notificationsGroup) => ({
        day: notificationsGroup[1][0].createdAt, // берем createdAt первого уведомления, т.к. они отсортированы по дням и тут неважно время
        data: notificationsGroup[1],
      }));
    });

    const setUnreadNotificationsCount = (unreadNotifications: number) => {
      unreadCount.value = unreadNotifications;
    };

    const setNotificationListVisibility = (isVisible: boolean) => {
      isNotificationsListVisible.value = isVisible;
    };

    const setBrowserTabWithUnreadNotifications = (value: boolean) => {
      isBrowserTabWithUnreadNotifications.value = value;
    };

    const setPersonalizedNotificationsVisibility = (isVisible: boolean) => {
      isPersonalizedNotificationsInfoVisible.value = isVisible;
    };

    const clearNotifications = () => {
      notifications.ids = null;
      notifications.data = null;
    };

    const loadNotifications = (unread: boolean, pagination: TPagination) => getNotifications(unread, pagination)
      .then((response: TReachedResponse<TNotificationsResponse>) => {
        if (!response.data) return;
        const { ids, data } = normalizePayloadToDataIds(response.data);
        if (Array.isArray(notifications.ids)) {
          notifications.ids = notifications.ids.concat(ids);
          notifications.data = {
            ...notifications.data,
            ...data,
          };
        } else {
          notifications.ids = ids;
          notifications.data = data;
        }
      })
      .catch((error: TDomainError) => Promise.reject(error));

    const loadUnreadNotificationsCount = () => getUnreadNotificationsCount()
      .then((response: TReachedResponse<TUnreadNotificationsCountResponse>) => {
        if (!response.data) return;
        setUnreadNotificationsCount(response.data.unreadCount);
      })
      .catch((error: TDomainError) => Promise.reject(error));

    const updateNotificationStatusById = (isReadStatus: boolean, notificationId: string) => {
      const request = isReadStatus
        ? readNotification(notificationId)
        : unreadNotification(notificationId);
      return request
        .then(() => {
          const currentNotification = notifications.data?.[notificationId];
          if (currentNotification) {
            currentNotification.isRead = isReadStatus;
          }

          if (isReadStatus) {
            if (unreadCount.value !== 0) {
              unreadCount.value -= 1;
            }
          } else {
            unreadCount.value += 1;
          }
        })
        .catch((error: TDomainError) => Promise.reject(error));
    };

    const $reset = () => {
      setUnreadNotificationsCount(0);
      isNotificationsListVisible.value = false;
      isBrowserTabWithUnreadNotifications.value = false;
      isPersonalizedNotificationsInfoVisible.value = true;
    };

    return {
      unreadCount,
      isNotificationsListVisible,
      isBrowserTabWithUnreadNotifications,
      notifications,
      isPersonalizedNotificationsInfoVisible,

      getGroupedNotificationsByDate,

      setUnreadNotificationsCount,
      setNotificationListVisibility,
      setBrowserTabWithUnreadNotifications,
      setPersonalizedNotificationsVisibility,
      clearNotifications,

      loadNotifications,
      loadUnreadNotificationsCount,
      updateNotificationStatusById,
      $reset,
    };
  },
  { persist: true });
